/**
 * NOYZ-440 (ADA): accessibility for the cart drawer discount field.
 *
 * The field is rendered by the third-party DCART "Discount in Cart" app (Preact) and is moved into
 * our promo slot by mountScDiscountInPromoSlot() in cart-drawer.js. We can't change the app, so
 * this file layers the missing behaviour on top of it:
 *
 *  - NOYZ-451  the input gets a descriptive name, and the "remove code" buttons get one too.
 *  - NOYZ-450  Apply / Enter on an empty field shows and announces an error (the app does nothing).
 *  - NOYZ-453  after a code is applied or removed the app re-renders the whole drawer through our
 *              renderContents(), which drops focus to <body> and closes the promo panel. We put
 *              focus back somewhere sensible and announce the result.
 *
 * Everything is re-applied from a MutationObserver because the app's markup is rebuilt each time
 * the drawer re-renders.
 */
(function () {
  if (window.cartDrawerPromoA11y) return;
  window.cartDrawerPromoA11y = true;

  // The DCART app's placement settings are keyed by theme ID. On any theme it doesn't know (a
  // preview, or a newly published theme) the discount field may never mount. This deferred script
  // runs after the app's inline config and before the app's own deferred scripts, so give unknown
  // themes the placement the live theme uses. Themes the app knows keep the app's own settings.
  (function setDiscountPlacementForUnknownThemes() {
    const placement = window.DCART?.discountbox?.placement;
    const themeId = String(window.Shopify?.theme?.id || '');
    if (!placement || !themeId || placement[themeId] || placement.theme === themeId) return;
    window.scThemeConfig = {
      cartDiscountSelector: { path: '#cart-page-progress div.drawer__progress-container h3.drawer__progress-title', type: 0 },
      cartTotalSelector: {
        path: '#main-cart-footer div.page-width div.cart__footer div.cart__blocks div.js-contents div.totals p.totals__total-value',
        type: 0,
      },
      ajaxDicountSelector: {
        path: '#CartDrawer div.drawer__inner div.drawer__footer div.cart-drawer__footer-promo div.cart-drawer__footer-promo-header',
        type: 1,
      },
      ajaxTotalSelector: {
        path: '#CartDrawer div.drawer__inner div.drawer__footer div.cart-drawer__footer div.totals p.totals__total-value',
        type: 0,
      },
    };
  })();

  const PENDING_TIMEOUT = 8000;
  // True from an empty submit until the shopper types or submits a code. While true our
  // "Enter a discount code." error wins over any message the app left from an earlier attempt.
  let blankActive = false;
  let pending = null; // { type: 'apply' | 'remove', app: Element, at: number, timer: number }
  let lastApp = null;
  let settleTimer = null;

  const drawer = () => document.querySelector('cart-drawer');
  const app = () => drawer()?.querySelector('[data-cart-drawer-promo-slot] #scDiscountApp');
  const input = () => app()?.querySelector('#code');
  const status = () => document.getElementById('CartDrawer-PromoStatus');
  const blankError = () => document.getElementById('CartDrawer-PromoError');
  const toggle = () => drawer()?.querySelector('.cart-drawer__footer-promo-toggle');

  function text(key, replacements = {}) {
    let value = status()?.dataset[key] || '';
    Object.entries(replacements).forEach(([token, replacement]) => {
      value = value.replace(`[${token}]`, replacement);
    });
    return value;
  }

  function announce(message) {
    const region = status();
    if (!region || !message) return;
    region.textContent = '';
    // A short gap makes screen readers treat a repeated message as new.
    setTimeout(() => {
      region.textContent = message;
    }, 100);
  }

  function appMessage() {
    return app()?.querySelector('.sc-message--error, .sc-message--warning') || null;
  }

  function appliedCodes() {
    return Array.from(app()?.querySelectorAll('.sc_simple-info__tag .code-name') || []).map((el) =>
      el.textContent.trim()
    );
  }

  function moneyFromRow(match) {
    const rows = Array.from(app()?.querySelectorAll('.sc_simple-info__row') || []);
    const row = rows.find((el) => match.test(el.textContent));
    return row?.querySelector('.money')?.textContent.trim() || '';
  }

  function setBlankError(show) {
    blankActive = show;
    const error = blankError();
    if (!error) return;
    const message = show ? text('empty') : '';
    if (error.textContent !== message) error.textContent = message;
    if (error.hidden === show) error.hidden = !show;
    // Hide the app's leftover message from an earlier attempt while ours is showing (CSS in
    // cart-drawer.liquid), so there is only ever one error on screen.
    error.closest('[data-cart-drawer-promo-slot]')?.classList.toggle('is-blank-error', show);
  }

  // --- NOYZ-451: names, and keeping the input's error state in sync -------------------------

  function patchApp() {
    const field = input();
    if (field) {
      const label = text('label');
      if (label && field.getAttribute('aria-label') !== label) field.setAttribute('aria-label', label);

      const message = appMessage();
      if (blankActive) {
        // Re-assert after a re-render, which brings back a fresh (hidden) error element.
        setBlankError(true);
        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', 'CartDrawer-PromoError');
      } else if (message) {
        if (!message.id) message.id = 'CartDrawer-PromoAppMessage';
        field.setAttribute('aria-invalid', message.classList.contains('sc-message--error') ? 'true' : 'false');
        field.setAttribute('aria-describedby', message.id);
      } else {
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-describedby');
      }
    }

    app()
      ?.querySelectorAll('.sc_simple-info__tag .sc-tag')
      .forEach((tag) => {
        const button = tag.querySelector('button');
        const code = tag.querySelector('.code-name')?.textContent.trim();
        const label = code ? text('remove', { code }) : '';
        if (button && label && button.getAttribute('aria-label') !== label) button.setAttribute('aria-label', label);
      });
  }

  // --- NOYZ-450: empty submit ----------------------------------------------------------------

  function handleBlankSubmit(event) {
    const field = input();
    if (!field || field.value.trim()) return false;
    event.preventDefault();
    event.stopPropagation();
    setBlankError(true);
    patchApp();
    announce(text('empty'));
    field.focus();
    return true;
  }

  // --- NOYZ-453: after the app re-renders the drawer ----------------------------------------

  function startPending(type) {
    clearPending();
    setBlankError(false);
    pending = { type, app: app(), at: Date.now() };
    // If the app updates in place without re-rendering the drawer, finish from what is on screen.
    pending.timer = setTimeout(() => finishPending(), PENDING_TIMEOUT);
  }

  function clearPending() {
    if (pending?.timer) clearTimeout(pending.timer);
    pending = null;
  }

  function openPanelAndFocusInput() {
    const button = toggle();
    // The toggle's own click handler (cart-drawer.js) opens the panel and focuses the input.
    if (button && button.getAttribute('aria-expanded') !== 'true') button.click();
    input()?.focus();
  }

  function finishPending() {
    if (!pending) return;
    const { type } = pending;
    clearPending();

    const cart = drawer();
    const message = appMessage();
    let target;
    let announcement;

    if (type === 'apply' && message) {
      // Keep the panel open so the error is visible, and return to the field to try again.
      openPanelAndFocusInput();
      target = input();
      announcement = message.textContent.trim();
    } else {
      // Success or removal: the panel stays as the re-render left it (closed), as it does today.
      // Focus goes to the visible "Add a promo code" button.
      const panelOpen = toggle()?.getAttribute('aria-expanded') === 'true';
      target = panelOpen ? input() : toggle();
      if (type === 'apply') {
        const codes = appliedCodes();
        const savings = moneyFromRow(/discount/i);
        const total = moneyFromRow(/total/i);
        announcement = [
          codes.length ? text('applied', { code: codes.join(', ') }) : '',
          savings ? text('savings', { amount: savings }) : '',
          total ? text('total', { total }) : '',
        ]
          .filter(Boolean)
          .join(' ');
      } else {
        announcement = text('removed');
      }
    }

    patchApp();
    if (target && cart) {
      // restoreCartFocus() in cart-drawer.js uses this if a follow-up re-render (e.g. a gift
      // being added for the new total) drops focus again shortly after.
      const returnFocus = () => (target.isConnected ? target : null);
      cart._getReturnFocus = returnFocus;
      setTimeout(() => {
        if (cart._getReturnFocus === returnFocus) cart._getReturnFocus = null;
      }, 6000);
      target.focus();
    }
    announce(announcement);
  }

  function onDrawerMutation() {
    patchApp();

    const current = app();
    if (current && current !== lastApp) {
      lastApp = current;
      if (pending && current !== pending.app) pending.remounted = true;
    }
    if (!pending?.remounted) return;

    // Wait for the re-render (and the app's own render into the new mount) to settle.
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      if (pending && app()?.querySelector('.sc_simple-container')) finishPending();
    }, 200);
  }

  function init() {
    const cart = drawer();
    if (!cart) return;

    lastApp = app();
    patchApp();
    new MutationObserver(onDrawerMutation).observe(cart, { childList: true, subtree: true });

    // Capture phase so these run before the app's own handlers on the button and input.
    cart.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('[data-cart-drawer-promo-slot] #sc-discount__submit')) {
          if (!handleBlankSubmit(event)) startPending('apply');
          return;
        }
        if (event.target.closest('[data-cart-drawer-promo-slot] .sc_simple-info__tag .sc-tag button')) {
          startPending('remove');
        }
      },
      true
    );

    cart.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' || !event.target.closest('[data-cart-drawer-promo-slot] #code')) return;
        if (!handleBlankSubmit(event)) startPending('apply');
      },
      true
    );

    // Capture phase: the app's own input handler stops the event from bubbling.
    cart.addEventListener(
      'input',
      (event) => {
        if (event.target.closest('[data-cart-drawer-promo-slot] #code') && !blankError()?.hidden) {
          setBlankError(false);
          patchApp();
        }
      },
      true
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
